import { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import {
    popupBannerWidget,
    popupBannerWidget_popupBannerWidget,
    popupBannerWidget_popupBannerWidget_display_on,
} from 'src/queries/__generated__/popupBannerWidget';
import { splitCurrencySpecific } from 'src/util/splitCurrencySpecific';

import popupBannerQuery from '../../queries/popupBannerWidget.graphql';

import { usePopupPageInfo, PopupPageInfo } from './PopupPageContext';

const STORAGE_KEY_PREFIX = 'popup_banner_dismissed_';

function getStorageKey(title: string): string {
    return `${STORAGE_KEY_PREFIX}${title.replace(/\s+/g, '_').toLowerCase()}`;
}

function isPopupDismissed(title: string): boolean {
    if (typeof window === 'undefined') return true;
    try {
        return sessionStorage.getItem(getStorageKey(title)) === '1';
    } catch {
        return false;
    }
}

function setPopupDismissed(title: string): void {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(getStorageKey(title), '1');
    } catch {
        // sessionStorage unavailable
    }
}

const CATEGORY_PAGE_GROUPS: string[] = ['anchor_categories', 'notanchor_categories'];
const PRODUCT_PAGE_GROUPS: string[] = [
    'configurable_products',
    'simple_products',
    'bundle_products',
    'wearer_bundle_products',
    'all_products',
];
const ALL_PAGES_GROUPS: string[] = ['all_pages', 'pages'];

function parseEntities(entities: string | null): number[] {
    if (!entities || entities.trim() === '') return [];
    return entities
        .split(',')
        .map((e) => parseInt(e.trim(), 10))
        .filter((n) => !isNaN(n));
}

function doesRuleMatchCategory(
    rule: popupBannerWidget_popupBannerWidget_display_on,
    categoryId: number | null,
    parentCategoryIds: number[],
): boolean {
    if (!rule.page_group || !CATEGORY_PAGE_GROUPS.includes(rule.page_group)) return false;
    if (categoryId === null) return false;

    if (rule.page_for === 'all') return true;

    if (rule.page_for === 'specific') {
        const entityIds = parseEntities(rule.entities);
        if (entityIds.includes(categoryId)) return true;
        if (parentCategoryIds.some((id) => entityIds.includes(id))) return true;
    }

    return false;
}

function doesRuleMatchProduct(rule: popupBannerWidget_popupBannerWidget_display_on, productId: number | null): boolean {
    if (!rule.page_group || !PRODUCT_PAGE_GROUPS.includes(rule.page_group)) return false;
    if (productId === null) return false;

    if (rule.page_for === 'all') return true;

    if (rule.page_for === 'specific') {
        const entityIds = parseEntities(rule.entities);
        return entityIds.includes(productId);
    }

    return false;
}

function doesRuleMatchProductByCategory(
    rule: popupBannerWidget_popupBannerWidget_display_on,
    productCategoryId: number | null,
): boolean {
    if (!rule.page_group || !CATEGORY_PAGE_GROUPS.includes(rule.page_group)) return false;
    if (productCategoryId === null) return false;

    if (rule.page_for === 'all') return true;

    if (rule.page_for === 'specific') {
        const entityIds = parseEntities(rule.entities);
        return entityIds.includes(productCategoryId);
    }

    return false;
}

/**
 * Check whether the first popup's display rules match the current page.
 * Priority: product-specific > category > all_pages.
 */
function doesPopupMatchPage(popup: popupBannerWidget_popupBannerWidget, pageInfo: PopupPageInfo): boolean {
    if (!popup.display_on) return false;

    let productMatch = false;
    let categoryMatch = false;
    let allPagesMatch = false;

    for (const rule of popup.display_on) {
        if (!rule) continue;

        if (pageInfo.type === 'product') {
            if (!productMatch && doesRuleMatchProduct(rule, pageInfo.entityId)) {
                productMatch = true;
            }
            if (!categoryMatch && doesRuleMatchProductByCategory(rule, pageInfo.categoryId)) {
                categoryMatch = true;
            }
        }

        if (pageInfo.type === 'category') {
            if (!categoryMatch && doesRuleMatchCategory(rule, pageInfo.categoryId, pageInfo.parentCategoryIds)) {
                categoryMatch = true;
            }
        }

        if (
            !allPagesMatch &&
            rule.page_for === 'all' &&
            rule.page_group &&
            ALL_PAGES_GROUPS.includes(rule.page_group)
        ) {
            allPagesMatch = true;
        }
    }

    return productMatch || categoryMatch || allPagesMatch;
}

export type PopupBannerData = {
    title: string;
    imageDesktop: string;
    imageMobile: string;
    link: string;
};

export function usePopupBanner() {
    const { pageInfo } = usePopupPageInfo();
    const { pathname } = useLocation();
    const isHomePage = pathname === '/';

    const { data: popupData } = useQuery<popupBannerWidget>(popupBannerQuery, {
        ssr: false,
    });

    const matchedPopup = useMemo((): PopupBannerData | null => {
        // Only the first popup of each group is considered, as before. One for the selected
        // currency wins when it fits the page; otherwise fall back to one for every currency.
        const { specific, generic } = splitCurrencySpecific(popupData?.popupBannerWidget);
        const popup = [specific[0], generic[0]].find(
            (candidate) => candidate && (isHomePage || doesPopupMatchPage(candidate, pageInfo)),
        );
        if (!popup) return null;

        if (!popup.widget_parameters?.image_desktop || !popup.widget_parameters?.image_mobile) return null;

        return {
            title: popup.title || '',
            imageDesktop: popup.widget_parameters.image_desktop,
            imageMobile: popup.widget_parameters.image_mobile,
            link: popup.widget_parameters.link || '',
        };
    }, [popupData, pageInfo, isHomePage]);

    const [isOpen, setIsOpen] = useState(false);
    const [wasDismissed, setWasDismissed] = useState(false);

    useEffect(() => {
        if (matchedPopup && !isPopupDismissed(matchedPopup.title)) {
            setIsOpen(true);
            setWasDismissed(false);
        } else if (matchedPopup && isPopupDismissed(matchedPopup.title)) {
            setIsOpen(false);
            setWasDismissed(true);
        } else {
            setIsOpen(false);
            setWasDismissed(false);
        }
    }, [matchedPopup]);

    const dismiss = useCallback(() => {
        if (matchedPopup) {
            setPopupDismissed(matchedPopup.title);
        }
        setIsOpen(false);
        setWasDismissed(true);
    }, [matchedPopup]);

    const reopen = useCallback(() => {
        setIsOpen(true);
    }, []);

    return {
        popup: matchedPopup,
        isOpen,
        wasDismissed,
        showRibbon: matchedPopup !== null && !isOpen,
        isProductPage: pageInfo.type === 'product',
        dismiss,
        reopen,
    };
}
